import { NgClass } from '@angular/common';
import { Component, inject, input, output, signal } from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	Validators,
} from '@angular/forms';

import { IPayment } from '@webapp/payments/domain/payments';
import { ButtonComponent } from '@webapp/ui';

@Component({
	selector: 'app-add-cbu',
	standalone: true,
	imports: [ReactiveFormsModule, NgClass, ButtonComponent],
	templateUrl: './add-cbu.component.html',
	styleUrl: './add-cbu.component.scss',
})
export class AddCbuComponent{
	private readonly formBuilder = inject(FormBuilder);
	needSecurityCode = input<boolean>();
	loading = input(false);
	readonly EventPaymentSubmit = output<IPayment>();
	readonly submitted = signal(false);

	paymentForm: FormGroup;
	cardLogo = 'images/bank.svg';

	constructor() {
		this.paymentForm = this.formBuilder.group({
			cbuNumber: ['', [Validators.required, this.cbuValidator.bind(this)]]
		});
	}

	cbuValidator(control: AbstractControl): ValidationErrors | null {
		const value = control.value?.replace(/\s/g, '') || '';

		if (!value) {
			return null; // Let required validator handle empty values
		}

		// Check if it contains only numbers
		if (!/^\d+$/.test(value)) {
			return { invalidFormat: true };
		}

		// Check if it has exactly 22 digits
		if (value.length !== 22) {
			return { invalidCard: true };
		}

		return null;
	}

	onCbuNumberInput(event: any) {
		let value = event.target.value.replace(/\D/g, ''); // Remove non-digits

		// Limit to 22 digits
		if (value.length > 22) {
			value = value.substring(0, 22);
		}

		// Format with spaces every 4 digits for better readability
		const formattedValue = value.replace(/(.{4})/g, '$1 ').trim();

		// Update the form control
		this.paymentForm.get('cbuNumber')?.setValue(formattedValue, { emitEvent: false });

		// Update the actual input value
		event.target.value = formattedValue;
	}

	onSubmit(): void {
		this.submitted.set(true);
		if (!this.paymentForm.valid) return;

		this.setPayment();
	}

	shouldShowError(controlName: string, errorName: string): boolean {
		const control = this.paymentForm.get(controlName);
		return (
			(this.submitted() ||
				((control?.touched ?? false) && (control?.dirty ?? false))) &&
			control?.hasError(errorName) === true
		);
	}

	setPayment(): void {
		const cbuNumber = this.paymentForm.get('cbuNumber')?.value.replace(/\s/g, '');

		const payment: IPayment = {
			number: cbuNumber,
			name: `CBU ${cbuNumber.substring(0, 4)}...${cbuNumber.substring(18)}`,
			method: 'cbu',
			exist: false,
			type: 'cbu'
		};

		this.EventPaymentSubmit.emit(payment);
	}
}
